import React from 'react';
import { Helmet } from 'react-helmet-async';
import Hero from '../../components/Hero';
import CallToAction from '../../components/CallToAction';
import '../ProjectPages/ProjectPages.css';

// Import images
import edtechHeroImage from '../../assets/images/edtech.jpg';

const EducationTechEN = () => {
  const faqData = [
    {
      question: "What is ProgenX Academy and how does it work?",
      answer: "ProgenX Academy is an AI-powered personalized learning platform. It analyzes each student's learning speed, style, and needs to offer them special educational programs. This way, each student discovers their potential and achieves success."
    },
    {
      question: "What does an AI-powered education platform mean?",
      answer: "Using AI technology, we identify students' strengths and weaknesses. We offer personalized learning paths for each student, provide instant feedback, and reinforce learning by suggesting similar questions."
    },
    {
      question: "How do students use the platform?",
      answer: "Students access specially prepared content by logging into the platform. They receive instant feedback while solving questions, track their progress, and learn with fun interactive content."
    },
    {
      question: "Can parents and teachers see student progress?",
      answer: "Yes, detailed reports are prepared for parents and teachers. It is clearly visible which topics progress has been made in and which areas need more work."
    },
    {
      question: "Is the education technology platform paid?",
      answer: "Our platform has different package options. You can contact us for detailed information and special offers."
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "ProgenX Academy",
    "applicationCategory": "EducationalApplication",
    "operatingSystem": "Web, iOS, Android",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "TRY"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "150"
    },
    "description": "AI-powered personalized learning platform. Offers special educational programs for each student."
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="project-page education-tech">
      <Helmet>
        <title>Education Technologies | AI-Powered Learning Platform - ProgenX Academy 2025</title>
        <meta name="description" content="ProgenX Academy AI-powered personalized learning platform. Special educational programs for each student, smart question solutions, real-time feedback. Turkey's most advanced education technology solution." />
        <meta name="keywords" content="ai education, personalized learning, smart question solving, online education platform, adaptive learning, education technology, progenx academy, ai-powered education, student success tracking, education software" />
        <meta property="og:title" content="Education Technologies | AI-Powered Learning - ProgenX" />
        <meta property="og:description" content="AI-powered learning platform offering special educational programs for each student. Increase success with personalized learning experience." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenxacademy.com/en/projects/education-technologies" />
        <meta property="og:image" content="https://progenxacademy.com/edtech.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://progenxacademy.com/en/projects/education-technologies" />
        <link rel="alternate" hreflang="tr" href="https://progenxacademy.com/projeler/egitim-teknolojileri" />
        <link rel="alternate" hreflang="en" href="https://progenxacademy.com/en/projects/education-technologies" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>

      <Hero 
        title="Education Technologies" 
        backgroundImage={edtechHeroImage}
      />

      <div className="project-content">
        <div className="container">
          <section className="project-overview">
            <h2>Personalized Education for Every Student</h2>
            <p>
              ProgenX Academy knows that every student is different and turns these differences into strengths. 
              Our AI-powered platform analyzes students' learning speeds, styles, and needs to offer 
              them special educational programs. This way, each student discovers their potential and achieves success.
            </p>
          </section>

          <section className="features">
            <h2>How Do We Help Students?</h2>
            <div className="features-grid">
              <div className="feature-item">
                <h3>Personalized Learning Experience</h3>
                <p>
                  Each student's learning speed and style is different. Our platform identifies 
                  students' strengths and weaknesses and offers them personalized learning paths. 
                  This way, each student progresses at their own pace and learns more effectively.
                </p>
              </div>

              <div className="feature-item">
                <h3>Instant Feedback and Guidance</h3>
                <p>
                  Students receive instant feedback while solving questions. They learn why they made mistakes, 
                  discover correct solution methods. The system reinforces learning by suggesting similar questions 
                  and increases success.
                </p>
              </div>

              <div className="feature-item">
                <h3>Detailed Progress Tracking</h3>
                <p>
                  Thanks to detailed reports prepared for students, parents, and teachers, 
                  it is clearly visible which topics progress has been made in and which areas need more work.
                </p>
              </div>

              <div className="feature-item">
                <h3>Fun and Interactive Content</h3>
                <p>
                  Interactive content that makes learning fun, gamification elements, and group work 
                  increase students' motivation and make the learning process more enjoyable.
                </p>
              </div>
            </div>
          </section>

          <section className="benefits">
            <h2>What Do We Provide for Students?</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <h3>Learning at Your Own Pace</h3>
                <p>Each student progresses at their own learning pace. No one falls behind, no one gets bored. 
                Everyone discovers their potential and reaches their best version.</p>
              </div>

              <div className="benefit-item">
                <h3>Faster and More Effective Learning</h3>
                <p>Thanks to personalized content and smart suggestions, students 
                learn more in less time and remember what they learn better.</p>
              </div>

              <div className="benefit-item">
                <h3>Continuous Development and Progress</h3>
                <p>With regular feedback and suggestions, students continuously improve. 
                They see how much they've progressed in which topic and reach their goals more easily.</p>
              </div>

              <div className="benefit-item">
                <h3>Experience That Makes You Love Learning</h3>
                <p>With fun content, success rewards, and interactive learning methods, 
                students' motivation increases and they love learning.</p>
              </div>
            </div>
          </section>

          <section className="faq-section">
            <h2>Frequently Asked Questions</h2>
            <div className="faq-container">
              {faqData.map((faq, index) => (
                <div key={index} className="faq-item">
                  <h3 className="faq-question">{faq.question}</h3>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>

      <CallToAction 
        title="A New Era in Education is Beginning"
        subtitle="Contact us for a personalized education experience where every student discovers their potential."
        buttonText="Get Started Now"
        buttonLink="/en/contact"
      />
    </div>
  );
};

export default EducationTechEN;
